import type { EnhancedStore } from "@reduxjs/toolkit";
import { configureStore } from "@reduxjs/toolkit";

import {
  eventPublishingListenerCreate,
  eventQueueCreate,
  nodeDesignsActionInterceptorMiddlewareCreate,
  revisionCaptureListener,
  serverActionDeliveryListenerCreate,
  unsentActionsStorageListenerCreate,
} from "./middleware/index.ts";
import type { NautilusStateAction } from "./nautilusActions.ts";
import type { NautilusDesignerState } from "./nautilusState.ts";
import { nautilusStateReducerCreate } from "./nautilusStateReducer.ts";
import type { NautilusStoreOptions } from "./shared.ts";

const noopUnsentActionsStorage = { saveUnsentActions: () => {}, clearUnsentActions: () => {} };

export const createNautilusStore = <AppConfig extends Record<string, unknown> = Record<string, unknown>>({
  createNodeDesignsReducer,
  sendActionToServer,
  publishNodeDesignsEvent,
  nodeDesignsActionInterceptors = [],
  middleware = [],
  createAppConfigReducer,
  onUnsentActionsChanged = noopUnsentActionsStorage,
}: NautilusStoreOptions<AppConfig>) => {
  const eventQueue = eventQueueCreate();
  return configureStore({
    reducer: nautilusStateReducerCreate<AppConfig>({
      nodeDesignsReducer: createNodeDesignsReducer({ notifyEvent: eventQueue.enqueue }),
      appConfigReducer: createAppConfigReducer(),
    }),
    // ahead of the interceptors: a middleware here reads the whole store, so it is
    // where a reaction that needs more than node designs — the app config a
    // manifest-derived answer stands on — belongs
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ serializableCheck: false, immutableCheck: false })
        .prepend(middleware)
        .concat(nodeDesignsActionInterceptorMiddlewareCreate({ interceptors: nodeDesignsActionInterceptors }))
        .concat(eventPublishingListenerCreate({ eventQueue, publishEvent: publishNodeDesignsEvent }).middleware)
        .concat(revisionCaptureListener.middleware)
        .concat(serverActionDeliveryListenerCreate({ sendActionToServer }).middleware)
        .concat(unsentActionsStorageListenerCreate(onUnsentActionsChanged).middleware),
  });
};

export type NautilusStore<AppConfig extends Record<string, unknown>> = EnhancedStore<
  NautilusDesignerState<AppConfig>,
  NautilusStateAction
>;
